import { type ReactNode, useEffect, useId, useRef } from 'react';
import { useTelegramBack } from '../telegram-back.js';
import { Icon } from './icons.js';

/*
 * Нижняя шторка поверх экрана: сведения, история, полный арт.
 *
 * Нативный <dialog> через showModal(): фокус заперт внутри, Esc
 * закрывает, фон недоступен для экранного диктора — без своей ловушки
 * фокуса. Системная «Назад» Telegram тоже закрывает шторку.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
  manageBack = true,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly manageBack?: boolean;
}): React.ReactElement {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useTelegramBack(open ? onClose : null, manageBack);

  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-labelledby={titleId}
      onClose={() => {
        // Отложенный close прежней шторки не закрывает уже повторно открытый dialog.
        if (open && !ref.current?.open) onClose();
      }}
    >
      {/* Подложка-кнопка: нажатие мимо панели закрывает шторку. */}
      <button type="button" className="sheet__scrim" aria-label="Закрыть" onClick={onClose} />
      <div className="sheet__panel">
        <span className="sheet__grip" aria-hidden="true" />
        <header className="sheet__head">
          <h2 className="h2" id={titleId}>
            {title}
          </h2>
          <button type="button" className="sheet__close" aria-label="Закрыть" onClick={onClose}>
            <Icon name="x" size={20} />
          </button>
        </header>
        <div className="sheet__body">{children}</div>
      </div>
    </dialog>
  );
}
